import { useState, type ReactNode } from "react";
import * as Collapsible from "@radix-ui/react-collapsible";
import { ChevronDown } from "lucide-react";
import { cn } from "../utils/cx.js";

export interface CollapseItem {
  key: string;
  label: ReactNode;
  children: ReactNode;
  disabled?: boolean;
}

export interface CollapseProps {
  items: CollapseItem[];
  defaultActiveKeys?: string[];
  accordion?: boolean;
  className?: string;
}

export function Collapse({ items, defaultActiveKeys, accordion, className }: CollapseProps) {
  const [openKeys, setOpenKeys] = useState<string[]>(defaultActiveKeys ?? []);
  const toggle = (key: string) =>
    setOpenKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : accordion ? [key] : [...prev, key],
    );

  return (
    <div className={cn("rounded-lg border", className)}>
      {items.map((it) => {
        const isOpen = openKeys.includes(it.key);
        return (
          <Collapsible.Root
            key={it.key}
            open={isOpen}
            onOpenChange={() => !it.disabled && toggle(it.key)}
            className="border-b last:border-b-0"
          >
            <Collapsible.Trigger
              disabled={it.disabled}
              className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium outline-none hover:bg-muted/50 disabled:opacity-50"
            >
              {it.label}
              <ChevronDown
                className={cn(
                  "size-4 shrink-0 text-muted-foreground transition-transform",
                  isOpen && "rotate-180",
                )}
              />
            </Collapsible.Trigger>
            <Collapsible.Content className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
              <div className="px-4 pb-3 text-sm text-muted-foreground">{it.children}</div>
            </Collapsible.Content>
          </Collapsible.Root>
        );
      })}
    </div>
  );
}
